import { get } from 'lodash-es';
import { match } from 'ts-pattern';

import { Option } from '../types/utility';
import { WorkflowDataResolverDependencies } from '../types/WorkflowDataResolver';
import {
  AnyDocument,
  Document,
  Evaluatable,
  EvaluatableValue,
  EvaluatableValueAt,
} from '../types/workflows';
import { applyTransformFuncs, evaluate } from './general';
import { getDocumentTargetAndLocationForPath } from './getDocumentTargetAndLocationForPath';

const extractValueFromDocument = (
  rootDocument: AnyDocument | undefined,
  attributeName: string,
  documentPath: string[],
): any => {
  const [target] = getDocumentTargetAndLocationForPath(
    rootDocument,
    documentPath,
  );
  const document = target as Document;

  if (document) {
    return get(document, attributeName);
  }

  console.warn(
    `Failed to get document | documentPath: ${documentPath}, attributeName: ${attributeName}`,
  );

  return undefined;
};

export const isValueEvaluatable = (e: Evaluatable): e is EvaluatableValue =>
  typeof e === 'object' && 'value' in e;

const isValueAtEvaluatable = (e: Evaluatable): e is EvaluatableValueAt =>
  typeof e === 'object' && 'path' in e;

export const isEvaluatable = (value: any): value is Evaluatable =>
  (typeof value === 'object' &&
    value !== null &&
    value.type &&
    (isValueEvaluatable(value) || isValueAtEvaluatable(value))) ||
  typeof value === 'function';

// Checks whether the string is exactly one placeholder like "{field}"
export const isSingleEvaluatableString = (value: string): boolean =>
  /^\{\w+\}$/.test(value);

const splitEvaluatableKey = (value: string): [string, string] => {
  const firstDotIndex = value.indexOf('.');
  return [
    value.slice(0, firstDotIndex),
    value.slice(firstDotIndex + 1, value.length),
  ];
};

export const getMappedSelectedOptions = (
  selectedOptions: any,
): { [key: string]: string } => {
  const mappedSelectedOptions: { [key: string]: string } = {};

  for (const [key, value] of Object.entries(selectedOptions)) {
    if (Array.isArray(value)) {
      value.forEach((option, index) => {
        mappedSelectedOptions[`${key}[${index}]`] = option.value;
      });
    } else if (value) {
      const singleValueOption = value as Option<string>;
      mappedSelectedOptions[key] = singleValueOption.value;
    }
  }

  return mappedSelectedOptions;
};

const extractAllNonDuplicateVariableNamesFromExpression = (
  expression: string,
) =>
  (expression.match(/\{(\w+(-\w+)?(\.[\w~]+(-\w+)?)+)\}/g) || [])
    .filter((value, idx, array) => array.indexOf(value) === idx)
    .map((key) => key.replace(/(\{|\})/g, ''));

export const resolveEvaluatable = (
  evaluatable: Evaluatable,
  {
    documentPath = [],
    getFormValue,
    rootDocument,
  }: WorkflowDataResolverDependencies,
  getRootFormValue?: (key: string) => any,
): any => {
  if (typeof evaluatable === 'function') {
    return evaluatable({
      getDocumentValue: (attributeName, docPath) =>
        extractValueFromDocument(rootDocument, attributeName, docPath),
      getFormValue: (attributeName) =>
        getFormValue && getFormValue(attributeName),
      getRootFormValue: (attributeName) =>
        getRootFormValue && getRootFormValue(attributeName),
      documentPath,
    });
  }

  if (evaluatable.type === 'valueAt') {
    const valueAt = extractValueFromDocument(
      rootDocument,
      evaluatable.path,
      documentPath,
    );
    return evaluatable.transformFuncs
      ? applyTransformFuncs(valueAt, evaluatable.transformFuncs)
      : valueAt;
  }

  const { transformFuncs, type, value } = evaluatable;

  const keys = extractAllNonDuplicateVariableNamesFromExpression(value);

  // Resolve attributes
  const attributes = keys.map((key) => {
    const [dataResolverKey, attributeName] = splitEvaluatableKey(key);

    return match(dataResolverKey)
      .with('form', () =>
        getFormValue ? getFormValue(attributeName) : undefined,
      )
      .otherwise(() =>
        extractValueFromDocument(rootDocument, attributeName, documentPath),
      );
  });

  let resolvedValue = keys.reduce((expression, key, idx) => {
    const attribute = attributes[idx];
    const serialised = Array.isArray(attribute)
      ? attribute.join("','")
      : (attribute ?? '');
    return expression.replace(new RegExp(`{${key}}`, 'g'), serialised);
  }, value);

  if (type === 'expression') {
    resolvedValue = evaluate(resolvedValue);
  }

  return transformFuncs
    ? applyTransformFuncs(resolvedValue, transformFuncs)
    : resolvedValue;
};
